import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { v4 as uuidv4 } from 'uuid';
import cookies from './cookies';

const SESSION_COOKIE_NAME = 'app_session';
const SESSION_STORAGE_KEY = 'app-session';
const FINGERPRINT_STORAGE_KEY = 'browser-fingerprint';
const SESSION_EXPIRY_DAYS = 365; // 1 year

interface SessionData {
  sessionId: string;
  fingerprint: string;
  createdAt: string;
}

/**
 * Generate a browser fingerprint using FingerprintJS
 * This creates a unique identifier based on browser/device characteristics
 */
async function generateFingerprint(): Promise<string> {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch (error) {
    console.error('Error generating fingerprint:', error);
    // Fallback to simple fingerprint based on user agent and screen
    return generateSimpleFingerprint();
  }
}

/**
 * Fallback fingerprint generation using basic browser properties
 */
function generateSimpleFingerprint(): string {
  const nav = window.navigator;
  const screen = window.screen;
  
  const data = [
    nav.userAgent,
    nav.language,
    screen.colorDepth,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `fp_${Math.abs(hash).toString(36)}`;
}

/**
 * Get or create session ID from localStorage
 */
function getSessionFromStorage(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
}

/**
 * Save session ID to localStorage
 */
function saveSessionToStorage(sessionId: string): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

/**
 * Get fingerprint from localStorage
 */
function getFingerprintFromStorage(): string | null {
  try {
    return localStorage.getItem(FINGERPRINT_STORAGE_KEY);
  } catch (error) {
    console.error('Error reading fingerprint from localStorage:', error);
    return null;
  }
}

/**
 * Save fingerprint to localStorage
 */
function saveFingerprintToStorage(fingerprint: string): void {
  try {
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint);
  } catch (error) {
    console.error('Error saving fingerprint to localStorage:', error);
  }
}

/**
 * Get session ID from cookie
 */
function getSessionFromCookie(): string | null {
  return cookies.get(SESSION_COOKIE_NAME) as string | null;
}

/**
 * Save session ID to cookie
 */
function saveSessionToCookie(sessionId: string): void {
  cookies.set(SESSION_COOKIE_NAME, sessionId, SESSION_EXPIRY_DAYS);
}

/**
 * Initialize or retrieve app session with fingerprinting
 * This function ensures a session exists in both localStorage and cookies
 * 
 * @returns Promise<SessionData> containing sessionId and fingerprint
 */
export async function initializeAppSession(): Promise<SessionData> {
  // Check if we're in the browser
  if (typeof window === 'undefined') {
    throw new Error('initializeAppSession can only be called in the browser');
  }

  // Try to get existing session from multiple sources
  let sessionId = getSessionFromStorage() || getSessionFromCookie();
  
  // If no session exists, create a new one
  if (!sessionId) {
    sessionId = uuidv4();
    console.log('Created new session:', sessionId);
  }

  // Save to both storage mechanisms for redundancy
  saveSessionToStorage(sessionId);
  saveSessionToCookie(sessionId);

  // Get or generate fingerprint
  let fingerprint = getFingerprintFromStorage();
  
  if (!fingerprint) {
    fingerprint = await generateFingerprint();
    saveFingerprintToStorage(fingerprint);
    console.log('Generated new fingerprint:', fingerprint);
  }

  return {
    sessionId,
    fingerprint,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Get the current app session (synchronous)
 * Returns null if session hasn't been initialized yet
 */
export function getAppSession(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Try localStorage first, then cookie
  return getSessionFromStorage() || getSessionFromCookie();
}

/**
 * Get the current browser fingerprint (synchronous)
 * Returns null if fingerprint hasn't been generated yet
 */
export function getBrowserFingerprint(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return getFingerprintFromStorage();
}

/**
 * Get complete session data including fingerprint
 */
export function getSessionData(): SessionData | null {
  const sessionId = getAppSession();
  const fingerprint = getBrowserFingerprint();

  if (!sessionId) {
    return null;
  }

  return {
    sessionId,
    fingerprint: fingerprint || 'unknown',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Clear all session data (useful for testing or user logout)
 */
export function clearSession(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(FINGERPRINT_STORAGE_KEY);
    cookies.remove(SESSION_COOKIE_NAME);
    console.log('Session cleared');
  } catch (error) {
    console.error('Error clearing session:', error);
  }
}
